import { getToken, setToken } from './authAPI';
import { API_BASE_URL } from './config';

// Helper function to get headers with auth token
const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`
});

// Helper function to handle API responses and 401 errors
const handleResponse = async (response) => {
  const data = await response.json();
  
  // Handle 401 Unauthorized - clear token and redirect
  if (response.status === 401) {
    console.warn('⚠️  Session expired. Please login again.');
    setToken(null);
    localStorage.removeItem('authToken');
    // Redirect to login page
    window.location.href = '/admin/login';
    throw new Error(data.message || 'Session expired. Please login again.');
  }
  
  // Handle other error responses
  if (!response.ok) {
    throw new Error(data.message || `API Error: ${response.status}`);
  }
  
  return data;
};

// Public Videos API
export const videosAPI = {
  getAllVideos: async (page = 1, limit = 10, category = 'All', search = '') => {
    try {
      let url = `${API_BASE_URL}/videos?page=${page}&limit=${limit}`;
      if (category && category !== 'All') url += `&category=${category}`;
      if (search) url += `&search=${search}`;
      
      const response = await fetch(url);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching videos:', error);
      throw error;
    }
  },

  getVideoById: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/videos/${id}`);
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching video:', error);
      throw error;
    }
  }
};

// Admin Videos API
export const adminVideosAPI = {
  getAllVideos: async (page = 1, limit = 10, category = 'All', search = '') => {
    try {
      let url = `${API_BASE_URL}/admin/videos?page=${page}&limit=${limit}`;
      if (category && category !== 'All') url += `&category=${category}`;
      if (search) url += `&search=${search}`;
      
      const response = await fetch(url, {
        headers: getHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error fetching admin videos:', error);
      throw error;
    }
  },

  addVideo: async (videoData) => {
    try {
      console.log('📤 Sending video data:', videoData);
      console.log('📡 URL:', `${API_BASE_URL}/admin/videos/add`);
      console.log('🔐 Token:', getToken() ? 'Present' : 'Missing');
      
      const response = await fetch(`${API_BASE_URL}/admin/videos/add`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(videoData)
      });
      
      console.log('📥 Response status:', response.status);
      const responseData = await response.json();
      console.log('📥 Response data:', responseData);
      
      if (!response.ok) {
        throw new Error(responseData.message || `API Error: ${response.status}`);
      }
      
      return responseData;
    } catch (error) {
      console.error('❌ Video submission error:', error);
      throw error;
    }
  },

  updateVideo: async (id, videoData) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/videos/update/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(videoData)
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error updating video:', error);
      throw error;
    }
  },

  deleteVideo: async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/videos/delete/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      return await handleResponse(response);
    } catch (error) {
      console.error('Error deleting video:', error);
      throw error;
    }
  },

  getDashboardStats: async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers: getHeaders()
      });
      const data = await handleResponse(response);
      return data;
    } catch (error) {
      console.error('Error fetching stats:', error);
      throw error;
    }
  }
};

// Admin Latest News Section API
export const adminLatestNewsAPI = {
  // Get available news for latest section
  getAvailableNews: async (page = 1, limit = 10, search = '') => {
    try {
      let url = `${API_BASE_URL}/news/admin/latest/available?page=${page}&limit=${limit}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;

      const response = await fetch(url, {
        headers: getHeaders()
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch available news');
      }

      return data;
    } catch (error) {
      console.error('Error fetching available news:', error);
      throw error;
    }
  },

  // Update latest news section
  updateLatestNews: async (selectedNewsIds) => {
    try {
      const response = await fetch(`${API_BASE_URL}/news/admin/latest/update`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ selectedNewsIds })
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update latest news');
      }

      return data;
    } catch (error) {
      console.error('Error updating latest news:', error);
      throw error;
    }
  }
};
